import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";

const NewLeadDialog = ({
  open,
  onClose,
  onSave,
  stages = [],
  segments = [],
  sources = [],
  initialStageId = null,
  currentUser = null,
}) => {
  const stageOptions = useMemo(() => stages || [], [stages]);
  const segmentOptions = useMemo(() => segments || [], [segments]);
  const sourceOptions = useMemo(() => sources || [], [sources]);

  const firstStageId = stageOptions.length > 0 ? stageOptions[0].id : null;

  const [values, setValues] = useState({
    name: "",
    phone: "",
    stageId: initialStageId ?? firstStageId ?? "",
    studentName: "",
    studentAge: "",
    segment: "",
    grade: "",
    shift: "",
    segmentId: "",
    source: "",
    sourceId: "",
    previousSchool: "",
    interestDetails: "",
    referredBy: "",
    motherName: "",
    motherPhone: "",
    fatherName: "",
    fatherPhone: "",
    nextFollowUpDate: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState({});

  useEffect(() => {
    if (!open) return;

    const resolvedStageId = initialStageId ?? firstStageId ?? "";
    setValues((prev) => ({
      ...prev,
      stageId: resolvedStageId,
      name: "",
      phone: "",
      studentName: "",
      studentAge: "",
      segment: "",
      grade: "",
      shift: "",
      segmentId: "",
      source: "",
      sourceId: "",
      previousSchool: "",
      interestDetails: "",
      referredBy: "",
      motherName: "",
      motherPhone: "",
      fatherName: "",
      fatherPhone: "",
      nextFollowUpDate: "",
    }));
    setFieldError({});
  }, [open, initialStageId, firstStageId]);

  const handleChange = (key) => (event) => {
    setValues((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const validate = () => {
    const errors = {};
    if (!values.name.trim()) errors.name = "Nome do responsável é obrigatório.";
    if (!values.phone.trim()) errors.phone = "Telefone é obrigatório.";
    if (!values.studentName.trim()) errors.studentName = "Nome do aluno é obrigatório.";
    if (!values.stageId) errors.stageId = "Estágio é obrigatório.";
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validate();
    setFieldError(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const selectedSegment = segmentOptions.find((s) => Number(s.id) === Number(values.segmentId));
      const selectedSource = sourceOptions.find((s) => Number(s.id) === Number(values.sourceId));

      let nextFollowUpDate = null;
      if (values.nextFollowUpDate) {
        const parts = String(values.nextFollowUpDate).split("-");
        if (parts.length === 3) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          const dt = new Date(y, m, d, 12, 0, 0);
          if (!Number.isNaN(dt.getTime())) {
            nextFollowUpDate = dt.toISOString();
          }
        }
      }

      const payload = {
        name: values.name.trim(),
        phone: values.phone.trim(),
        stageId: Number(values.stageId),
        responsibleUserId: currentUser?.id ?? null,

        studentName: values.studentName.trim(),
        studentAge: values.studentAge ? Number(values.studentAge) : null,
        segment: selectedSegment?.name ?? null,
        grade: values.grade ? String(values.grade).trim() : null,
        shift: values.shift ? String(values.shift).trim() : null,

        segmentId: values.segmentId ? Number(values.segmentId) : null,
        source: selectedSource?.name ?? null,
        sourceId: values.sourceId ? Number(values.sourceId) : null,

        previousSchool: values.previousSchool ? values.previousSchool.trim() : null,
        interestDetails: values.interestDetails ? values.interestDetails.trim() : null,
        referredBy: values.referredBy ? values.referredBy.trim() : null,

        motherName: values.motherName ? values.motherName.trim() : null,
        motherPhone: values.motherPhone ? values.motherPhone.trim() : null,
        fatherName: values.fatherName ? values.fatherName.trim() : null,
        fatherPhone: values.fatherPhone ? values.fatherPhone.trim() : null,
        nextFollowUpDate,
      };

      await onSave(payload);
      onClose();
    } catch (err) {
      // erro tratado no componente pai
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { maxHeight: "82vh" } }}
    >
      <DialogTitle>Novo Lead</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1.25}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome do Responsável *"
              value={values.name}
              onChange={handleChange("name")}
              error={Boolean(fieldError.name)}
              helperText={fieldError.name ?? ""}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Telefone *"
              value={values.phone}
              onChange={handleChange("phone")}
              error={Boolean(fieldError.phone)}
              helperText={fieldError.phone ?? ""}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Nome do Aluno *"
              value={values.studentName}
              onChange={handleChange("studentName")}
              error={Boolean(fieldError.studentName)}
              helperText={fieldError.studentName ?? ""}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="dense">
              <InputLabel id="lead-segment-label">Segmento</InputLabel>
              <Select
                labelId="lead-segment-label"
                value={values.segmentId}
                label="Segmento"
                onChange={handleChange("segmentId")}
                size="small"
              >
                <MenuItem value="">
                  <em>Selecione um segmento</em>
                </MenuItem>
                {segmentOptions.map((seg) => (
                  <MenuItem key={seg.id} value={seg.id}>
                    {seg.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Série"
              value={values.grade}
              onChange={handleChange("grade")}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Turno"
              value={values.shift}
              onChange={handleChange("shift")}
              select
              fullWidth
              margin="dense"
              size="small"
            >
              <MenuItem value="">
                <em>Selecione um turno</em>
              </MenuItem>
              <MenuItem value="Manhã">Manhã</MenuItem>
              <MenuItem value="Tarde">Tarde</MenuItem>
              <MenuItem value="Noite">Noite</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Escola Anterior"
              value={values.previousSchool}
              onChange={handleChange("previousSchool")}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome da Mãe"
              value={values.motherName}
              onChange={handleChange("motherName")}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Telefone da Mãe"
              value={values.motherPhone}
              onChange={handleChange("motherPhone")}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome do Pai"
              value={values.fatherName}
              onChange={handleChange("fatherName")}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Telefone do Pai"
              value={values.fatherPhone}
              onChange={handleChange("fatherPhone")}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Detalhes de Interesse"
              value={values.interestDetails}
              onChange={handleChange("interestDetails")}
              fullWidth
              margin="dense"
              multiline
              minRows={2}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="dense">
              <InputLabel id="lead-source-label">Fonte</InputLabel>
              <Select
                labelId="lead-source-label"
                value={values.sourceId}
                label="Fonte"
                onChange={handleChange("sourceId")}
                size="small"
              >
                <MenuItem value="">
                  <em>Selecione uma fonte</em>
                </MenuItem>
                {sourceOptions.map((src) => (
                  <MenuItem key={src.id} value={src.id}>
                    {src.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Indicado por"
              value={values.referredBy}
              onChange={handleChange("referredBy")}
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="dense" error={Boolean(fieldError.stageId)}>
              <InputLabel id="lead-stage-label">Estágio</InputLabel>
              <Select
                labelId="lead-stage-label"
                value={values.stageId}
                label="Estágio"
                onChange={handleChange("stageId")}
                size="small"
              >
                {stageOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {fieldError.stageId ? (
              <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>
                {fieldError.stageId}
              </div>
            ) : null}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Próximo Acompanhamento"
              type="date"
              value={values.nextFollowUpDate}
              onChange={handleChange("nextFollowUpDate")}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Idade do Aluno"
              value={values.studentAge}
              onChange={handleChange("studentAge")}
              fullWidth
              margin="dense"
              type="number"
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={submitting} variant="contained" color="primary">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewLeadDialog;

